import { useCallback, useState } from "react";

import { useGetDataObjectWorkflowRelationChain } from "@certa/queries/hooks/dataObjects.hooks";
import { useEnableDataObjects } from "@certa/common/hooks/useEnableDataObjects";
import { useGetOrphanKinds } from "@certa/common/hooks/useGetOrphanKinds";

import type { Kind } from "@certa/types";
import type { CreateDataObjectWorkflowRelationActionsModelReturn } from "@certa/queries/types/dataObjectsAPIs.types";

type UseWorkflowInitiatorProps = {
  workflowKind?: Kind;
  onClose: () => void;
  onCreateWorkflow: (
    workflowKindTag: string,
    name?: string,
    parent?: number | null,
    objectId?: string
  ) => void;
};

/**
 * Hook to handle workflow initiation logic, including data object relations
 * @param props Configuration options
 * @param props.onClose Callback function to close the workflow initiator (browse all modal)
 * @param props.onCreateWorkflow Callback function to create a new workflow with optional parameters
 * @returns Object containing workflow initiator state and handlers
 */
export const useWorkflowInitiator = (props: UseWorkflowInitiatorProps) => {
  const { onClose, onCreateWorkflow } = props;

  const isDataObjectsEnabled = useEnableDataObjects();

  const orphanKinds = useGetOrphanKinds();

  const [selectedWorkflowKind, setSelectedWorkflowKind] = useState<
    Kind | undefined
  >();
  const [selectedObjectInstanceId, setSelectedObjectInstanceId] = useState<
    string | undefined
  >();

  const [
    canShowStartWorkflowFromObjectDialog,
    setCanShowStartWorkflowFromObjectDialog
  ] = useState(false);

  const [
    canShowStartChildWorkflowWithParentDialog,
    setCanShowStartChildWorkflowWithParentDialog
  ] = useState(false);

  const {
    data: objectTypeWorkflowRelations,
    isLoading: isObjectTypeWorkflowRelationsLoading
  } = useGetDataObjectWorkflowRelationChain(
    {
      relation: "initializer_object",
      workflowKindTag: selectedWorkflowKind?.tag as string
    },
    {
      enabled: !!selectedWorkflowKind && isDataObjectsEnabled,
      cacheTime: 0,
      onSuccess: data => {
        if (!selectedWorkflowKind) return;

        handleDataObjectsWorkflowInitiation(selectedWorkflowKind, data);
      }
    }
  );

  const handleCloseStartWorkflowFromObjectDialog = useCallback(() => {
    setSelectedWorkflowKind(undefined);
    setCanShowStartWorkflowFromObjectDialog(false);
  }, []);

  const handleCreateWorkflow = useCallback(
    (workflowKind: Kind, objectId?: string) => {
      onCreateWorkflow(workflowKind.tag, undefined, null, objectId);
      handleCloseStartWorkflowFromObjectDialog();
      onClose();
    },
    [onCreateWorkflow, handleCloseStartWorkflowFromObjectDialog, onClose]
  );

  const handleDataObjectsWorkflowInitiation = (
    selectedWorkflowKind: Kind,
    objectTypeWorkflowRelations: CreateDataObjectWorkflowRelationActionsModelReturn
  ) => {
    if (objectTypeWorkflowRelations?.length) {
      const canAutoCreateWorkflow =
        selectedObjectInstanceId &&
        objectTypeWorkflowRelations.length <= 2 &&
        objectTypeWorkflowRelations.find(
          relation =>
            relation.relation === "initializer_object" &&
            relation.workflowKindTag === selectedWorkflowKind.tag
        );

      if (canAutoCreateWorkflow) {
        handleCreateWorkflow(selectedWorkflowKind, selectedObjectInstanceId);
      } else {
        setCanShowStartWorkflowFromObjectDialog(true);
      }
    } else {
      // No object relations, start workflow directly
      onCreateWorkflow(selectedWorkflowKind.tag);
      handleCloseStartWorkflowFromObjectDialog();
    }
  };

  const handleStandardWorkflowInitiation = useCallback(
    (selectedWorkflowKind: Kind) => {
      const isRelatedNonOrphanKind =
        selectedWorkflowKind.isRelatedKind &&
        !orphanKinds.includes(selectedWorkflowKind.tag);

      if (isRelatedNonOrphanKind) {
        setCanShowStartChildWorkflowWithParentDialog(true);
      } else {
        onCreateWorkflow(selectedWorkflowKind.tag);
        setSelectedWorkflowKind(undefined);
      }
    },
    [onCreateWorkflow, orphanKinds]
  );

  const handleCloseStartChildWorkflowWithParentDialog = useCallback(() => {
    setSelectedWorkflowKind(undefined);
    setCanShowStartChildWorkflowWithParentDialog(false);
  }, []);

  const handleSelectWorkflowKind = useCallback(
    (workflowKind: Kind, objectInstanceId?: string) => {
      setSelectedWorkflowKind(workflowKind);
      if (isDataObjectsEnabled) {
        setSelectedObjectInstanceId(objectInstanceId);
      } else {
        handleStandardWorkflowInitiation(workflowKind);
      }
    },
    [isDataObjectsEnabled, handleStandardWorkflowInitiation]
  );

  return {
    objectTypeWorkflowRelations,
    isObjectTypeWorkflowRelationsLoading,
    selectedWorkflowKind,
    onSelectWorkflowKind: handleSelectWorkflowKind,
    canShowStartWorkflowFromObjectDialog,
    closeStartWorkflowFromObjectDialog:
      handleCloseStartWorkflowFromObjectDialog,
    handleCreateWorkflow,
    canShowStartChildWorkflowWithParentDialog,
    closeStartChildWorkflowWithParentDialog:
      handleCloseStartChildWorkflowWithParentDialog
  };
};
